/*
 * HALT interaction layer.
 * - Header: sticky elevation, desktop disclosure dropdowns, search overlay,
 *   mobile menu drawer, cart-drawer hand-off.
 * - Reveal-on-scroll for below-fold home content.
 * Native Shopify forms, predictive search and cart drawer remain Dawn's.
 * Everything is delegated from `document` (added once), so theme-editor
 * section reloads need only a cheap, idempotent re-scan.
 */
(() => {
  if (window.Halt && window.Halt.ready) return;

  const doc = document;
  const root = doc.documentElement;
  // Gate for JS-only presentation (disclosure hiding, reveal). Dawn hard-codes
  // `js` on <html>, so a separate class keeps no-JS fallbacks honest.
  root.classList.add("halt-js");
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  const FOCUSABLE = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "summary",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const isVisible = (el) =>
    !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length) &&
    getComputedStyle(el).visibility !== "hidden";
  const focusables = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.closest("[hidden]") && isVisible(el),
    );

  /* ---------------------------------------------------------------------
   * Dialogs (search overlay + menu drawer). One open at a time.
   * ------------------------------------------------------------------- */
  let active = null; // { el, trigger }

  const dialogSurface = (el) => el.querySelector("[role='dialog']") || el;

  function setExpanded(id, value) {
    doc
      .querySelectorAll(`[aria-controls="${id}"][aria-expanded]`)
      .forEach((t) => t.setAttribute("aria-expanded", String(value)));
  }

  function openDialog(el, trigger) {
    if (!el) return;
    if (active && active.el === el) return;
    if (active) closeDialog({ restoreFocus: false });
    closeDisclosures();
    active = { el, trigger: trigger || doc.activeElement };
    el.classList.add("is-open");
    setExpanded(el.id, true);
    root.classList.add("halt-scroll-lock");
    const target =
      el.querySelector("[data-halt-autofocus]") ||
      focusables(dialogSurface(el))[0] ||
      dialogSurface(el);
    // visibility flips synchronously (0s), so focus can move immediately.
    requestAnimationFrame(() => target.focus({ preventScroll: true }));
  }

  function closeDialog({ restoreFocus = true } = {}) {
    if (!active) return;
    const { el, trigger } = active;
    active = null;
    el.classList.remove("is-open");
    setExpanded(el.id, false);
    root.classList.remove("halt-scroll-lock");
    const predictive = el.querySelector("predictive-search");
    if (predictive && typeof predictive.close === "function")
      predictive.close();
    if (
      restoreFocus &&
      trigger &&
      doc.contains(trigger) &&
      isVisible(trigger)
    ) {
      trigger.focus({ preventScroll: true });
    } else if (restoreFocus) {
      // Trigger gone (e.g. drawer → search): fall back to the menu toggle.
      const fallback = doc.querySelector(".halt-menu-toggle");
      if (fallback && isVisible(fallback))
        fallback.focus({ preventScroll: true });
    }
  }

  function trapTab(event) {
    if (!active) return;
    const surface = dialogSurface(active.el);
    const items = focusables(surface);
    if (!items.length) {
      event.preventDefault();
      surface.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const current = doc.activeElement;
    if (!surface.contains(current)) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
    } else if (event.shiftKey && current === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* ---------------------------------------------------------------------
   * Desktop nav disclosures
   * ------------------------------------------------------------------- */
  function setDisclosure(item, open) {
    const toggle = item.querySelector("[data-halt-disclosure-toggle]");
    item.classList.toggle("is-open", open);
    if (toggle) toggle.setAttribute("aria-expanded", String(open));
  }
  function closeDisclosures(except) {
    doc.querySelectorAll("[data-halt-disclosure].is-open").forEach((item) => {
      if (item !== except) setDisclosure(item, false);
    });
  }

  /* ---------------------------------------------------------------------
   * Cart drawer hand-off (Dawn's <cart-drawer>)
   * ------------------------------------------------------------------- */
  function openCartDrawer(returnTo) {
    const drawer = doc.querySelector("cart-drawer");
    if (!drawer || typeof drawer.open !== "function") return false;
    drawer.open(returnTo);
    return true;
  }

  /* ---------------------------------------------------------------------
   * Delegated events (bound once)
   * ------------------------------------------------------------------- */
  doc.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest("[data-halt-dialog-open]");
    if (opener) {
      const dialog = doc.getElementById(
        opener.getAttribute("data-halt-dialog-open"),
      );
      if (dialog) {
        event.preventDefault();
        // From inside the drawer, hand the "return focus" to the drawer's trigger.
        const returnTo =
          active && active.el.contains(opener) ? active.trigger : opener;
        if (active && active.el === dialog) closeDialog();
        else openDialog(dialog, returnTo);
        return;
      }
    }

    const closer = target.closest("[data-halt-close]");
    if (closer && active && active.el.contains(closer)) {
      event.preventDefault();
      closeDialog();
      return;
    }

    const cartOpener = target.closest("[data-halt-cart-open]");
    if (cartOpener) {
      const returnTo =
        doc.getElementById("cart-icon-bubble") ||
        (active ? active.trigger : cartOpener);
      if (active) closeDialog({ restoreFocus: false });
      if (openCartDrawer(returnTo)) event.preventDefault();
      return;
    }

    const toggle = target.closest("[data-halt-disclosure-toggle]");
    if (toggle) {
      const item = toggle.closest("[data-halt-disclosure]");
      const open = !item.classList.contains("is-open");
      closeDisclosures(item);
      setDisclosure(item, open);
      return;
    }

    if (!target.closest("[data-halt-disclosure]")) closeDisclosures();
  });

  doc.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      if (active) {
        event.preventDefault();
        closeDialog();
        return;
      }
      const open = doc.querySelector("[data-halt-disclosure].is-open");
      if (open) {
        setDisclosure(open, false);
        open.querySelector("[data-halt-disclosure-toggle]")?.focus();
      }
      return;
    }
    if (event.key === "Tab" && active) {
      trapTab(event);
      return;
    }
    // Anchors acting as buttons respond to Space.
    if (
      (event.key === " " || event.key === "Spacebar") &&
      event.target instanceof Element &&
      event.target.matches("a[role='button']")
    ) {
      event.preventDefault();
      event.target.click();
    }
  });

  // Close a disclosure when focus leaves it.
  doc.addEventListener("focusout", (event) => {
    const item =
      event.target instanceof Element &&
      event.target.closest("[data-halt-disclosure].is-open");
    if (!item) return;
    const next = event.relatedTarget;
    if (next instanceof Node && item.contains(next)) return;
    if (next) setDisclosure(item, false);
  });

  // Close the drawer if the viewport grows past the mobile breakpoint.
  window.matchMedia("(min-width: 990px)").addEventListener("change", (mq) => {
    if (mq.matches && active && active.el.classList.contains("halt-drawer")) {
      closeDialog({ restoreFocus: false });
    }
  });

  /* ---------------------------------------------------------------------
   * Sticky header elevation
   * ------------------------------------------------------------------- */
  let ticking = false;
  function updateHeader() {
    ticking = false;
    const scrolled = window.scrollY > 8;
    doc
      .querySelectorAll("[data-halt-header]")
      .forEach((h) => h.classList.toggle("is-scrolled", scrolled));
  }
  window.addEventListener(
    "scroll",
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateHeader);
      }
    },
    { passive: true },
  );

  /* ---------------------------------------------------------------------
   * Reveal on scroll. Below-fold only, once, JS-gated, reduced-motion aware.
   * Sections opt in with data-halt-reveal-group (children) or ="self".
   * A fail-safe timer guarantees nothing stays hidden.
   * ------------------------------------------------------------------- */
  const REVEAL_GROUP_ATTR = "data-halt-reveal-group";
  const REVEAL_FAILSAFE_MS = 1200;
  const motionOff = root.getAttribute("data-halt-motion") === "off";
  const revealObserver =
    "IntersectionObserver" in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.remove("is-pending");
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
        )
      : null;

  function revealAll(scope = doc) {
    scope.querySelectorAll(".halt-reveal.is-pending").forEach((el) => {
      el.classList.remove("is-pending");
      revealObserver && revealObserver.unobserve(el);
    });
  }

  function revealTargets(scope) {
    const out = [];
    scope.querySelectorAll(`[${REVEAL_GROUP_ATTR}]`).forEach((group) => {
      if (group.getAttribute(REVEAL_GROUP_ATTR) === "self") out.push(group);
      else out.push(...Array.from(group.children));
    });
    return out;
  }

  let failsafe = null;
  function armFailsafe() {
    clearTimeout(failsafe);
    failsafe = setTimeout(() => revealAll(), REVEAL_FAILSAFE_MS);
  }

  function initReveal(scope = doc) {
    // Fail-safe: skip in the theme editor, with reduced motion, without IO,
    // when the merchant turned motion off, and under automation.
    if (
      !revealObserver ||
      motionOff ||
      reducedMotion.matches ||
      designMode ||
      navigator.webdriver
    )
      return;
    const fold = window.innerHeight;
    revealTargets(scope).forEach((el, index) => {
      if (el.dataset.haltReveal) return; // idempotent
      el.dataset.haltReveal = "1";
      // Never hide anything already on screen (hero, first fold).
      if (el.getBoundingClientRect().top < fold) return;
      el.classList.add("halt-reveal", "is-pending");
      el.style.setProperty("--halt-reveal-delay", `${Math.min(index % 3, 2) * 40}ms`);
      revealObserver.observe(el);
    });
    // Whatever the observer misses is shown shortly after the first scroll.
    window.addEventListener("scroll", armFailsafe, { once: true, passive: true });
  }

  reducedMotion.addEventListener("change", () => {
    if (reducedMotion.matches) {
      revealAll();
      doc
        .querySelectorAll(".halt-hero video")
        .forEach((video) => video.pause());
    }
  });
  window.addEventListener("beforeprint", () => revealAll());

  /* ---------------------------------------------------------------------
   * Init + theme editor lifecycle
   * ------------------------------------------------------------------- */
  function initScope(scope = doc) {
    // Progressive enhancement: links that now open dialogs act as buttons.
    scope.querySelectorAll("a[data-halt-dialog-open]").forEach((a) => {
      if (doc.getElementById(a.getAttribute("data-halt-dialog-open"))) {
        a.setAttribute("role", "button");
      }
    });
    initReveal(scope);
  }

  function boot() {
    initScope(doc);
    updateHeader();
  }

  doc.addEventListener("shopify:section:load", (event) => {
    const section = event.target;
    initScope(section);
    updateHeader();
    // Dawn's cart drawer binds #cart-icon-bubble only once, at definition time.
    if (section.querySelector("#cart-icon-bubble")) {
      const drawer = doc.querySelector("cart-drawer");
      if (
        drawer &&
        typeof drawer.setHeaderCartIconAccessibility === "function"
      ) {
        drawer.setHeaderCartIconAccessibility();
      }
    }
  });

  doc.addEventListener("shopify:section:unload", (event) => {
    const section = event.target;
    if (active && section.contains(active.el))
      closeDialog({ restoreFocus: false });
    section
      .querySelectorAll(".halt-reveal")
      .forEach((el) => revealObserver?.unobserve(el));
  });

  if (doc.readyState === "loading")
    doc.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();

  window.Halt = { ready: true, openDialog, closeDialog, revealAll, REVEAL_FAILSAFE_MS, REVEAL_GROUP_ATTR };
})();
